import { useSetWatch, useUnwatch, useWatchlist } from "../api/queries";
import type { OfferMode } from "../api/types";

/** Watch or unwatch one (item, mode), from wherever that pair is on screen.
 *
 *  It reads the whole watchlist rather than taking a boolean prop: the query
 *  is cached under one key and shared by every star on the page, so a hundred
 *  of them cost one request, and starring somewhere else in the app updates
 *  this one through the same invalidation.
 *
 *  A star and a word, never a star alone. Which state the glyph means — is ★
 *  "watched" or "click to watch"? — is exactly the ambiguity a label removes,
 *  and it is what the accessible name is built from.
 *
 *  `item` is the name, and it is required because the word alone does not
 *  identify anything. Item.tsx renders two of these in one `<dl>` and
 *  Market.tsx can have three side panels open at once; the pair each one
 *  stands for lives in the surrounding band or `<dt>`, which is near it on
 *  screen and nowhere at all in a list of buttons. The visible word stays the
 *  first token of the label, so the name still contains what the eye reads
 *  (WCAG 2.5.3). */
export function WatchStar({ itemId, item, mode }: {
  itemId: number; item: string; mode: OfferMode;
}) {
  const watchlist = useWatchlist();
  const set = useSetWatch();
  const unwatch = useUnwatch();

  // Undefined while the list is in flight: the star renders unpressed rather
  // than flickering from watched to unwatched once the answer lands.
  const watched = (watchlist.data?.rows ?? []).some(
    (row) => row.item_id === itemId && row.mode === mode);
  const busy = set.isPending || unwatch.isPending;
  // A click that did nothing has to say so. `busy` goes back to false and
  // `watched` sits at its pre-click value when a write fails, which without
  // this is indistinguishable from having missed the button. Watch.tsx says
  // the same thing about the same two hooks, in the band its panel can afford;
  // a control inside a title band cannot, so it says it in a word. Local to
  // this star, unlike that page's shared mutation: the hooks are called here.
  const failed = set.isError || unwatch.isError;

  return (
    <span className="star-wrap">
      <button
        type="button"
        className="star"
        aria-pressed={watched}
        aria-label={`${watched ? "watching" : "watch"} ${item} ${mode}`}
        disabled={busy || !watchlist.data}
        onClick={() => (watched
          ? unwatch.mutate({ itemId, mode })
          : set.mutate({ itemId, mode }))}
      >
        <span aria-hidden="true">{watched ? "★" : "☆"}</span>
        {watched ? "watching" : "watch"}
      </button>
      {failed ? (
        <span className="star-failed" role="alert">not saved</span>
      ) : null}
    </span>
  );
}
